// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";


const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const xIsAuthenticated = session.isAuthenticated && !session.isGuest;
    const  currentBillTo = getBillToState(state, session.billToId).value;
    return {
        firstName: session?.firstName,
        customerNumber: currentBillTo?.customerNumber,
        xIsAuthenticated,
        dashboardUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,

    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface AccountOverviewStyles {
    wrapper?: InjectableCss;
    titleSection?: InjectableCss;
    dashboardSection?: InjectableCss;
    greetingTitle?: TypographyPresentationProps;
    firstName?: TypographyPresentationProps;
    customerNumber?: TypographyPresentationProps;
    link?: LinkPresentationProps;

}

export const accountOverviewStyles: AccountOverviewStyles = {};

const AccountOverview: React.FunctionComponent<Props> = ({
    firstName,
    customerNumber,
    xIsAuthenticated,
    dashboardUrl,
}: Props) => {
    const styles = accountOverviewStyles;

    if (!xIsAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleSection}>
                <Typography {...styles.greetingTitle} data-test-selector="AccountOverview_greeting">
                    {siteMessage("Custom_AccountOverview_Greeting")}
                    {firstName && ","}
                </Typography>
                {firstName && (
                    <Typography {...styles.firstName} data-test-selector="AccountOverview_firstName">
                        {firstName}
                    </Typography>
                )}
                {customerNumber && (
                    <Typography {...styles.customerNumber} data-test-selector="AccountOverview_customerNumber">
                        #{customerNumber}
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.dashboardSection}>
                <Link href={dashboardUrl} {...styles.link}>
                    {translate("Visit My Dashboard")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AccountOverview),
    definition: {
        group: "XNGAGE_CUSTOM_WIDGET" as any,
        displayName: "Account Overview",
    },
};

export default widgetModule;
